import React, { Suspense } from 'react';
import { Routes, Route, Link, Navigate, useLocation, NavLink } from 'react-router-dom';
import './App.css';

// Critical components (loaded immediately)
import GeoAdvisoryBanner from './components/GeoAdvisoryBanner';

// Lazy-loaded components for better performance
const SimpleAdminDashboard = React.lazy(() => import('./components/SimpleAdminDashboard'));
const RequireRole = React.lazy(() => import('./components/guards/RequireRole'));
const AccessDenied = React.lazy(() => import('./components/AccessDenied'));
const AdminPage = React.lazy(() => import('./pages/AdminPage'));
const PartnerDashboard = React.lazy(() => import('./components/WhiteLabelPartnerDashboard'));
const ReportBuilderInterface = React.lazy(() => import('./components/ReportBuilderInterface'));
const AnalyticsDashboard = React.lazy(() => import('./components/AnalyticsDashboard'));
const PremiumAnalytics = React.lazy(() => import('./components/PremiumAnalytics'));
const ThreatIndex = React.lazy(() => import('./components/ThreatIndex'));
const SDKLandingPage = React.lazy(() => import('./landing/SDKLandingPage'));
const Partners = React.lazy(() => import('./pages/Partners'));
const RequirePartnerKey = React.lazy(() => import('./components/guards/RequirePartnerKey'));
const ProAPIDocs = React.lazy(() => import('./pages/ProAPIDocs'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const DocumentationHub = React.lazy(() => import('./pages/DocumentationHub'));
// New public pages
const BlogPage = React.lazy(() => import('./pages/BlogPage'));
const PressKitPage = React.lazy(() => import('./pages/PressKitPage'));
const CareersPage = React.lazy(() => import('./pages/CareersPage'));
const FeaturesPage = React.lazy(() => import('./pages/FeaturesPage'));

// Loading component for better UX
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="animate-pulse flex flex-col items-center">
      <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
      <p className="mt-2 text-sm text-gray-600 dark:text-halo-ink-muted">Loading...</p>
    </div>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

function App() {
  const loc = useLocation();
  const fullBleed = loc.pathname.startsWith('/partners');
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  React.useEffect(() => {
    // Non-blocking auth check with intelligent preloading
    const checkAuth = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000); // 2s timeout
        
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, { 
          credentials: 'include',
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (response.status === 401 || response.status === 403) {
          if (!loc.pathname.includes('/admin/access-denied')) {
            window.location.replace('/#/admin/access-denied');
          }
        } else if (response.ok) {
          // Preload critical routes based on current location
          preloadRoutes(loc.pathname);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('Auth check failed:', error);
        }
      }
    };
    
    checkAuth();
  }, [loc.pathname]);

  // Intelligent route preloading
  const preloadRoutes = (currentPath) => {
    const routeMap = {
      '/': ['/analytics', '/premium-analytics'],
      '/analytics': ['/premium-analytics', '/reports'], 
      '/premium-analytics': ['/analytics', '/reports'],
      '/reports': ['/analytics', '/partners'],
      '/partners': ['/reports', '/admin'],
      '/admin': ['/analytics', '/partners']
    };
    
    const preloadList = routeMap[currentPath] || [];
    preloadList.forEach(route => {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = `#${route}`;
      document.head.appendChild(link);
    });
  };

  return (
    <div className="min-h-screen bg-halo-bg">
      <header className="bg-halo-surface/90 backdrop-blur-md shadow-sm border-b border-halo-border sticky top-0 z-50 transition-all duration-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold text-halo-ink hover:text-halo-accent transition-colors">Veridavo</Link>
            
            {/* Desktop Navigation */}
            <nav className="top-nav hidden md:flex items-center">
              <NavLink 
                to="/analytics" 
                className={({ isActive }) => 
                  `mr-8 text-sm font-medium transition-all duration-200 relative ${
                    isActive 
                      ? 'text-indigo-600 font-semibold' 
                      : 'text-gray-700 hover:text-gray-900'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    Analytics
                    {isActive && (
                      <span className="absolute -bottom-4 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-halo-accent rounded-full"></span>
                    )}
                  </>
                )}
              </NavLink>
              <NavLink 
                to="/premium-analytics" 
                className={({ isActive }) => 
                  `mr-8 text-sm font-medium transition-all duration-200 relative ${
                    isActive 
                      ? 'text-indigo-600 font-semibold' 
                      : 'text-gray-700 hover:text-gray-900'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    Premium Analytics
                    {isActive && (
                      <span className="absolute -bottom-4 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-halo-accent rounded-full"></span>
                    )}
                  </>
                )}
              </NavLink>
              <NavLink 
                to="/reports" 
                className={({ isActive }) => 
                  `mr-8 text-sm font-medium transition-all duration-200 relative ${
                    isActive 
                      ? 'text-indigo-600 font-semibold' 
                      : 'text-gray-700 hover:text-gray-900'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    Reports
                    {isActive && (
                      <span className="absolute -bottom-4 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-halo-accent rounded-full"></span>
                    )}
                  </>
                )}
              </NavLink>
              <NavLink 
                to="/partners" 
                className={({ isActive }) => 
                  `mr-8 text-sm font-medium transition-all duration-200 relative ${
                    isActive 
                      ? 'text-indigo-600 font-semibold' 
                      : 'text-gray-700 hover:text-gray-900'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    Partners
                    {isActive && (
                      <span className="absolute -bottom-4 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-halo-accent rounded-full"></span>
                    )}
                  </>
                )}
              </NavLink>
              <NavLink 
                to="/admin" 
                className={({ isActive }) => 
                  `text-sm font-medium transition-all duration-200 relative ${
                    isActive 
                      ? 'text-indigo-600 font-semibold' 
                      : 'text-gray-700 hover:text-gray-900'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    Admin
                    {isActive && (
                      <span className="absolute -bottom-4 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-halo-accent rounded-full"></span>
                    )}
                  </>
                )}
              </NavLink>
            </nav>
            
            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-gray-700 dark:text-halo-ink-muted hover:text-indigo-600 dark:hover:text-halo-accent hover:bg-gray-100 dark:hover:bg-halo-surface-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle navigation menu"
            >
              <svg className="w-5 h-5 transform transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} 
                  className="transition-all duration-200"
                />
              </svg>
            </button>
          </div>
          
          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-200 dark:border-halo-border">
              <nav className="flex flex-col space-y-1 pt-4">
                <NavLink 
                  to="/analytics" 
                  className={({ isActive }) => 
                    `block px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isActive 
                        ? 'bg-indigo-50 text-indigo-700 border-l-4 border-indigo-600 font-semibold' 
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`
                  }
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Analytics
                </NavLink>
                <NavLink 
                  to="/premium-analytics" 
                  className={({ isActive }) => 
                    `block px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isActive 
                        ? 'bg-indigo-50 text-indigo-700 border-l-4 border-indigo-600 font-semibold' 
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`
                  }
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Premium Analytics
                </NavLink>
                <NavLink 
                  to="/reports" 
                  className={({ isActive }) => 
                    `block px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isActive 
                        ? 'bg-indigo-50 text-indigo-700 border-l-4 border-indigo-600 font-semibold' 
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`
                  }
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Reports
                </NavLink>
                <NavLink 
                  to="/partners" 
                  className={({ isActive }) => 
                    `block px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isActive 
                        ? 'bg-indigo-50 text-indigo-700 border-l-4 border-indigo-600 font-semibold' 
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`
                  }
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Partners
                </NavLink>
                <NavLink 
                  to="/admin" 
                  className={({ isActive }) => 
                    `block px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isActive 
                        ? 'bg-indigo-50 text-indigo-700 border-l-4 border-indigo-600 font-semibold' 
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`
                  }
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Admin
                </NavLink>
              </nav>
            </div>
          )}
        </div>
      </header>
      <main className={fullBleed ? "px-0 py-0" : "max-w-7xl mx-auto px-4 py-6"}>
        {!fullBleed && <GeoAdvisoryBanner />}
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Home defaults to Branded Landing */}
            <Route index element={<SDKLandingPage />} />
            <Route path="/" element={<SDKLandingPage />} />

            {/* Key sections (support both with and without leading slash for HashRouter edge cases) */}
            <Route path="analytics" element={<AnalyticsDashboard />} />
            <Route path="/analytics" element={<AnalyticsDashboard />} />

            <Route path="premium-analytics" element={<PremiumAnalytics />} />
            <Route path="/premium-analytics" element={<PremiumAnalytics />} />

            <Route path="threat-index" element={<ThreatIndex />} />
            <Route path="/threat-index" element={<ThreatIndex />} />

            <Route path="reports" element={<ReportBuilderInterface />} />
            <Route path="/reports" element={<ReportBuilderInterface />} />

            {/* Partners flow (gated) */}
            <Route path="partners" element={<Partners />} />
            <Route path="/partners" element={<Partners />} />
            <Route path="partners/dashboard" element={<RequirePartnerKey><PartnerDashboard /></RequirePartnerKey>} />
            <Route path="/partners/dashboard" element={<RequirePartnerKey><PartnerDashboard /></RequirePartnerKey>} />

            {/* Admin */}
            <Route path="admin/access-denied" element={<AccessDenied />} />
            <Route path="admin/:tab" element={<RequireRole roles={['admin','owner']}><AdminPage /></RequireRole>} />
            <Route path="/admin/:tab" element={<RequireRole roles={['admin','owner']}><AdminPage /></RequireRole>} />
            <Route path="admin" element={<RequireRole roles={['admin','owner']}><AdminPage /></RequireRole>} />
            <Route path="/admin" element={<RequireRole roles={['admin','owner']}><AdminPage /></RequireRole>} />

            {/* Docs Viewer (no nav link; direct URL only) */}
            <Route path="docs/pro-api" element={<ProAPIDocs />} />
            <Route path="/docs/pro-api" element={<ProAPIDocs />} />
            <Route path="docs" element={<DocumentationHub />} />
            <Route path="/docs" element={<DocumentationHub />} />
            <Route path="docs/*" element={<DocumentationHub />} />
            <Route path="/docs/*" element={<DocumentationHub />} />

            {/* Public Pages */}
            <Route path="pricing" element={<PricingPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="blog" element={<BlogPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="press" element={<PressKitPage />} />
            <Route path="/press" element={<PressKitPage />} />
            <Route path="careers" element={<CareersPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="features" element={<FeaturesPage />} />
            <Route path="/features" element={<FeaturesPage />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}

export default App;